// Achievement, streak, holiday, scribe and adventure toasts — each one a message on the rail (ui/messageRail.js: bottom-centre,
// one at a time, a × on every card). Legendary achievement unlocks ALSO get a story beat (emitted from achievementManager).

import { Events, on } from './eventBus.js'
import { AchievementRarity } from '../config/balance.js'
import { formatCoins } from './format.js'
import { postMessage } from './messageRail.js'

const TOAST_LIFE_MS = 7000


// Every toast is a message on the rail (bottom-centre, one at a time): a badge, a title, a line.
function mountToast(badge, title, desc, lifeMs = TOAST_LIFE_MS) {
  postMessage({ kind: 'info', type: 'TOAST', icon: '🏆', title: `${badge} · ${title}`, text: desc, lifeMs })
}
function showToast({ name, desc, rarity }) {
  const r = AchievementRarity[rarity] ?? AchievementRarity.common
  mountToast(r.label, name, desc)
}

// Streak-reward toast — same stack as achievements but with a flame badge
// instead of a rarity color. The wealth bump itself happens elsewhere; this
// is just the celebratory surface.
function showStreakRewardToast({ dayNum, amount }) {
  mountToast(`DAY ${dayNum}`, '🔥 Login streak reward', `+${formatCoins(amount)} for logging in ${dayNum} days in a row.`)
}

// Holiday participation toast — fires when the server stamps a new
// holiday token on the user record (first auth-hit during the window).
function showHolidayToast({ holiday }) {
  if (!holiday) return
  mountToast('FESTIVAL', `${holiday.emoji ?? '🎉'} ${holiday.label}`, `+${Math.round((holiday.clickMultiplier - 1) * 100)}% clicks · streak rewards ×${holiday.streakBonusMult ?? 1}`, TOAST_LIFE_MS + 1500)
}

// Scribe message toast — fired by adventureManager while the cat is away.
// Parchment-style amber border, no rarity badge, kind drives the lead icon.
const SCRIBE_ICON = {
  pigeon:  '🐦',
  smoke:   '💨',
  courier: '🏇',
}
function showScribeToast({ kind, message }) {
  mountToast('SCRIBE', `${SCRIBE_ICON[kind] ?? '✉️'} Word from home`, message, TOAST_LIFE_MS + 500)
}

// Adventure discovery toast — the cat found something.
function showDiscoveryToast({ item, amount }) {
  mountToast('DISCOVERY', `✨ ${item}`, `Your cat found something. +${Number(amount ?? 0).toLocaleString()}m on return.`)
}

// Adventure outcome toast — fired when a quest ends (complete / recall / abandon) so the player always sees what they earned or lost.
function showAdventureEndToast({ reason, payout = 0, fine = 0 }) {
  const spec = reason === 'abandon'
    ? { badge: 'ABANDONED', icon: '🏃', title: 'Adventure abandoned', desc: `Haul forfeited. −${formatCoins(fine)} scramble fine. The cat is home.` }
    : reason === 'recall'
    ? { badge: 'RECALLED', icon: '🐾', title: 'Cat recalled', desc: `+${formatCoins(payout)} (70% of the haul). Relics kept.` }
    : { badge: 'COMPLETE', icon: '🎒', title: 'Adventure complete', desc: `+${formatCoins(payout)} and relics secured. Welcome home.` }
  mountToast(spec.badge, `${spec.icon} ${spec.title}`, spec.desc, TOAST_LIFE_MS + 500)
}

export function init() {
  on(Events.AchievementUnlocked, showToast)
  on(Events.StreakRewardClaimed, showStreakRewardToast)
  on(Events.HolidayParticipationStamp, showHolidayToast)
  on(Events.AdventureScribeBeat, showScribeToast)
  on(Events.AdventureDiscovery, showDiscoveryToast)
  on(Events.AdventureEnd, showAdventureEndToast)
}
